import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Psychology as PsychologyIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  InsertDriveFile as FileIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import ragService, {
  RagDocument,
  RagStats,
  RagWorkspace,
  RagChatHistorie,
  RagChatRequest,
  RagChatResponse,
} from '../services/ragService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Wissensdatenbank: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [stats, setStats] = useState<RagStats | null>(null);
  const [, setWorkspace] = useState<RagWorkspace | null>(null);
  const [history, setHistory] = useState<RagChatHistorie[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatResponse, setChatResponse] = useState<RagChatResponse | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [docsData, statsData, workspaceData, historyData] = await Promise.all([
        ragService.getDocuments(),
        ragService.getStats(),
        ragService.getWorkspace(),
        ragService.getHistory(20),
      ]);

      setDocuments(docsData);
      setStats(statsData);
      setWorkspace(workspaceData);
      setHistory(historyData);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await ragService.uploadDocument(selectedFile);
      setUploadDialogOpen(false);
      setSelectedFile(null);
      loadData();
    } catch (error) {
      console.error('Upload-Fehler:', error);
      alert('Fehler beim Hochladen der Datei');
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) return;

    try {
      await ragService.deleteDocument(documentId);
      loadData();
    } catch (error) {
      console.error('Lösch-Fehler:', error);
      alert('Fehler beim Löschen des Dokuments');
    }
  };

  const handleChat = async () => {
    if (!chatMessage.trim()) return;

    setChatLoading(true);
    try {
      const request: RagChatRequest = {
        message: chatMessage,
        mode: 'chat',
      };
      const response = await ragService.chat(request);
      setChatResponse(response);
      setChatMessage('');
      loadData(); // Reload to update history
    } catch (error) {
      console.error('Chat-Fehler:', error);
      alert('Fehler beim Chat');
    } finally {
      setChatLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Wissensdatenbank (RAG)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Hilfe & Anleitung zur Wissensdatenbank">
            <IconButton
              color="info"
              onClick={() => navigate('/wissensdatenbank/hilfe')}
              size="large"
            >
              <HelpIcon />
            </IconButton>
          </Tooltip>
          <input
            accept=".pdf,.docx,.txt,.md"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="file-upload">
            <Tooltip title="Laden Sie PDF, Word, Text oder Markdown-Dateien hoch">
              <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                Dokument hochladen
              </Button>
            </Tooltip>
          </label>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Anzahl der hochgeladenen Dokumente und verwendeter Speicherplatz">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    <FileIcon fontSize="small" /> Dokumente
                  </Typography>
                  <Typography variant="h4">{stats.totalDocuments}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stats.storageFormatted}
                  </Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Gesamtanzahl aller Chat-Anfragen seit Beginn">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    <PsychologyIcon fontSize="small" /> Chats gesamt
                  </Typography>
                  <Typography variant="h4">{stats.totalChats}</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Chat-Aktivität der letzten 7 Tage">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    <TrendingUpIcon fontSize="small" /> Letzte 7 Tage
                  </Typography>
                  <Typography variant="h4">{stats.chatsLast7d}</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Durchschnittliche Zeit bis zur Antwort - niedrigere Werte sind besser">
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Ø Antwortzeit
                  </Typography>
                  <Typography variant="h4">{Math.round(stats.avgResponseTimeMs)}ms</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tooltip title="Stellen Sie Fragen an Ihre Wissensdatenbank">
            <Tab label="Chat" icon={<PsychologyIcon />} iconPosition="start" />
          </Tooltip>
          <Tooltip title="Verwalten Sie Ihre hochgeladenen Dokumente">
            <Tab label="Dokumente" icon={<DescriptionIcon />} iconPosition="start" />
          </Tooltip>
          <Tooltip title="Sehen Sie alle bisherigen Chat-Anfragen und Antworten">
            <Tab label="Historie" icon={<HistoryIcon />} iconPosition="start" />
          </Tooltip>
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Chat */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fragen Sie die Wissensdatenbank
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Stellen Sie spezifische Fragen zu Ihren hochgeladenen Dokumenten. Die KI durchsucht Ihre Wissensdatenbank und liefert Antworten mit Quellenangaben.
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Tooltip title="Geben Sie Ihre Frage ein. Drücken Sie Enter zum Senden oder Shift+Enter für eine neue Zeile.">
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Beispiel: Was sind die Hauptthemen in den Dokumenten?"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                disabled={chatLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (chatMessage.trim() && !chatLoading) {
                      handleChat();
                    }
                  }
                }}
              />
            </Tooltip>
          </Box>
          <Tooltip title={!chatMessage.trim() ? "Bitte geben Sie eine Frage ein" : "Senden Sie Ihre Frage an die KI"}>
            <span>
              <Button
                variant="contained"
                onClick={handleChat}
                disabled={!chatMessage.trim() || chatLoading}
                startIcon={chatLoading ? <LinearProgress /> : <PsychologyIcon />}
              >
                {chatLoading ? 'Verarbeite...' : 'Frage stellen'}
              </Button>
            </span>
          </Tooltip>

          {chatResponse && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={chatResponse.success ? 'info' : 'error'}>
                <Typography variant="subtitle2">Antwort:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                  {chatResponse.response}
                </Typography>

                {chatResponse.sources && chatResponse.sources.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption">Quellen:</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {chatResponse.sources.map((source, idx) => (
                        <Chip
                          key={idx}
                          label={source.documentName}
                          size="small"
                          sx={{ mr: 0.5, mt: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Antwortzeit: {chatResponse.responseTimeMs}ms
                </Typography>
              </Alert>
            </Box>
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Documents */}
        {loading ? (
          <LinearProgress />
        ) : documents.length === 0 ? (
          <Alert severity="info">
            Noch keine Dokumente hochgeladen. Laden Sie Dokumente hoch, um die Wissensdatenbank zu füllen.
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Dokumente durchsuchen"
                placeholder="Nach Dateiname, Typ oder Benutzer suchen..."
                value={documentSearchQuery}
                onChange={(e) => setDocumentSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
              />
            </Box>
            <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Dateiname</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Größe</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Hochgeladen</TableCell>
                  <TableCell>Von</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents
                  .filter((doc) => {
                    if (!documentSearchQuery) return true;
                    const query = documentSearchQuery.toLowerCase();
                    return (
                      doc.originalDateiname.toLowerCase().includes(query) ||
                      doc.dateiTyp.toLowerCase().includes(query) ||
                      doc.hochgeladenVon.toLowerCase().includes(query) ||
                      doc.status.toLowerCase().includes(query)
                    );
                  })
                  .map((doc) => (
                  <TableRow key={doc.dokumentID}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileIcon fontSize="small" />
                        {doc.originalDateiname}
                      </Box>
                    </TableCell>
                    <TableCell>{doc.dateiTyp.toUpperCase()}</TableCell>
                    <TableCell>{formatBytes(doc.dateiGroesse)}</TableCell>
                    <TableCell>
                      <Chip
                        label={doc.status}
                        size="small"
                        color={doc.status === 'verarbeitet' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(doc.hochgeladenAm)}</TableCell>
                    <TableCell>{doc.hochgeladenVon}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(doc.dokumentID)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {documents.filter((doc) => {
              if (!documentSearchQuery) return true;
              const query = documentSearchQuery.toLowerCase();
              return (
                doc.originalDateiname.toLowerCase().includes(query) ||
                doc.dateiTyp.toLowerCase().includes(query) ||
                doc.hochgeladenVon.toLowerCase().includes(query) ||
                doc.status.toLowerCase().includes(query)
              );
            }).length} von {documents.length} Dokumenten angezeigt
          </Typography>
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* History */}
        <Paper>
          {history.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">Noch keine Chat-Historie vorhanden.</Alert>
            </Box>
          ) : (
            <List>
              {history.map((item, idx) => (
                <React.Fragment key={item.chatID}>
                  {idx > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle2" color="primary">
                            Frage: {item.anfrage}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {item.antwort}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          {formatDate(item.erstelltAm)} • {item.benutzer} •{' '}
                          {item.dauerMs ? `${item.dauerMs}ms` : ''} •{' '}
                          {item.erfolg ? 'Erfolg' : 'Fehler'}
                        </React.Fragment>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </TabPanel>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Dokument hochladen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Datei: {selectedFile?.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Größe: {selectedFile ? formatBytes(selectedFile.size) : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleUpload} variant="contained">
            Hochladen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Wissensdatenbank;
